import { useMemo } from 'react';

import { skipToken } from '@reduxjs/toolkit/dist/query';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';

import { AxiosBaseQueryFunction } from '../../axiosBaseQuery';
import { useSelectedStore } from '../stores-api/useSelectedStore';
import {
  CfiCompany,
  CfiCompanyQueryParams,
  useFetchCfisQuery,
} from './cfisService';

import { shouldFetchFn } from '~/api/utils';
import useFetchedEstimate from '~/core/hooks/useFetchedEstimate';
import { useSelectedColor } from '~/core/hooks/useSelectedColor';
import { useAppSelector } from '~/redux/hooks';
import { selectSelectedCustomerData } from '~/redux/slices/customerData';
import { selectIsSavingEstimate } from '~/redux/slices/estimate';
import { selectZipcode } from '~/redux/slices/zipcode';

type useCfisReturn = TypedUseQueryHookResult<
  CfiCompany[],
  CfiCompanyQueryParams,
  AxiosBaseQueryFunction
> & {
  cfi?: CfiCompany;
};

export const useCfis = (): useCfisReturn => {
  const customerData = useAppSelector(selectSelectedCustomerData);
  const zipCode = useAppSelector(selectZipcode);
  const selectedStore = useSelectedStore();
  const { selectedValue: selectedColor } = useSelectedColor();
  const isSavingEstimate = useAppSelector(selectIsSavingEstimate);
  const { fetchedEstimate } = useFetchedEstimate();

  const bestZipCode = useMemo(() => {
    if (isSavingEstimate && customerData?.zip && customerData.zip.length >= 5) {
      return customerData?.zip;
    }

    return fetchedEstimate?.customerZip ?? zipCode;
  }, [
    customerData?.zip,
    fetchedEstimate?.customerZip,
    isSavingEstimate,
    zipCode,
  ]);

  const shouldFetch = shouldFetchFn({
    args: [zipCode, selectedStore, selectedColor],
  });

  const { cfi, ...rest } = useFetchCfisQuery(
    shouldFetch &&
      zipCode &&
      selectedStore?.companyId &&
      selectedColor?.qteGrpID
      ? {
          params: {
            zipCode: bestZipCode,
            storeId: selectedStore.companyId,
            prodQteGrpID: selectedColor.qteGrpID,
            // ...installAddress, // TODO: figure out what to do with errors, skip the exact lookup for now
          },
        }
      : skipToken,
    {
      selectFromResult: (result) => ({
        cfi: result.isError ? undefined : result.data?.[0],
        ...result,
      }),
    }
  );

  return { cfi, ...rest };
};
